import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getActiveProductsTypeCodes } from 'pages/dashboard/components/Progress/selectors';
import { getIsNMIMerchant } from 'pages/dashboard/components/MerchantServices/selectors';
import { getBusiness, getBusinessSignatory } from 'pages/Profile/selectors';
import { useAppDispatch, useAppSelector } from 'hooks';
import { ProductTypeCodes } from 'entities/progress';
import { ChevronRightIcon } from 'components/icons';
import Button from 'components/Button';

import { setIsModalOpened } from '../submitDashboardSlice';
import { getIsNMIAgreed, getIsConfirmEnabled } from '../selectors';

type ModalActionsProps = {
  step: number;
  handleSubmit: () => void;
  handleBack: () => void;
  handleNext: () => void;
  isLoading: boolean;
};

const ModalActions: React.FC<ModalActionsProps> = ({
  step,
  handleSubmit,
  handleBack,
  handleNext,
  isLoading,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const signatory = useAppSelector(getBusinessSignatory);
  const isNMIAgreed = useAppSelector(getIsNMIAgreed);
  const isNMIMerchant = useAppSelector(getIsNMIMerchant);
  const isConfirmEnabled = useAppSelector(getIsConfirmEnabled);
  const activeProductTypes = useAppSelector(getActiveProductsTypeCodes);
  const business = useAppSelector(getBusiness);

  const [isAcceptStep, setIsAcceptStep] = useState(false);

  useEffect(() => {
    const el = document.getElementById('accept-terms');
    setIsAcceptStep(el != null);
  }, [step]);

  const showConnectorsStep = activeProductTypes.includes(
    ProductTypeCodes.payments,
  );
  const showLineOfCredit = activeProductTypes.includes(
    ProductTypeCodes.capital,
  );

  const stepsLength = useMemo(() => {
    let steps = 2;

    if (showConnectorsStep) {
      steps += 2;
    }

    if (showLineOfCredit) {
      steps += 1;
    }

    return steps;
  }, [showConnectorsStep, showLineOfCredit]);

  const handleDone = () => {
    dispatch(setIsModalOpened(false));
  };

  return (
    <>
      {step > 1 && step <= stepsLength && (
        <Button
          onClick={handleBack}
          variant="secondary-outline"
          className="w-48"
        >
          <ChevronRightIcon className="mr-3 rotate-180" />
          {t('common:back')}
        </Button>
      )}

      {step < stepsLength - (showLineOfCredit ? 1 : 0) && !isAcceptStep && (
        <Button className="w-48 float-right" onClick={handleNext}>
          {t('common:next')}
        </Button>
      )}

      {isAcceptStep && (
        <Button
          className="w-48 float-right"
          onClick={handleSubmit}
          loading={isLoading}
          disabled={
            !signatory || !isConfirmEnabled || (isNMIMerchant && !isNMIAgreed)
          }
        >
          {t(`common:${showLineOfCredit ? 'next' : 'confirm'}`)}
        </Button>
      )}

      {showLineOfCredit && step == (showConnectorsStep ? 4 : 3) && (
        <Button
          className="w-48 float-right"
          onClick={handleNext}
          disabled={!business.oatFiBusinessExternalId}
        >
          {t('common:confirm')}
        </Button>
      )}

      {step > stepsLength - (showConnectorsStep ? 1 : 0) && (
        <Button className="w-48 float-right" onClick={handleDone}>
          {t('common:done')}
        </Button>
      )}
    </>
  );
};

export default ModalActions;
